import { useErrorNotification } from '@providers/ErrorsNotificationProvider';
import {
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { components, DELETE, GET, paths, POST } from '@features/shared/api/club/clients';
import {
  ParticipantDto,
  ParticipantStatus,
  UpdateTournamentRequest,
} from '@features/shared/api/typings';
import { ParticipantAux } from '@features/tournament/types';

export const QUERY_KEYS = {
  tournamentAll: (clubId: string, tournamentId: string) => ['TOURNAMENT', clubId, tournamentId],
  tournamentCard: (clubId: string, tournamentId: string) => [
    ...QUERY_KEYS.tournamentAll(clubId, tournamentId),
    'CARD',
  ],
  tournamentResultsAll: (clubId: string, tournamentId: string) => [
    ...QUERY_KEYS.tournamentAll(clubId, tournamentId),
    'RESULTS',
  ],
  tournamentResultsRoundRobin: (clubId: string, tournamentId: string) => [
    ...QUERY_KEYS.tournamentResultsAll(clubId, tournamentId),
    'ROUND_ROBIN',
  ],
  tournamentResultsSwiss: (clubId: string, tournamentId: string) => [
    ...QUERY_KEYS.tournamentResultsAll(clubId, tournamentId),
    'SWISS',
  ],
  tournamentResultsSwissTeam: (clubId: string, tournamentId: string) => [
    ...QUERY_KEYS.tournamentResultsAll(clubId, tournamentId),
    'SWISS_TEAM',
  ],
  tournamentParticipants: (
    clubId: string,
    tournamentId: string,
    options?: paths['/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/participants']['get']['parameters']['query'],
  ) =>
    [...QUERY_KEYS.tournamentAll(clubId, tournamentId), 'ACTIVE_PARTICIPANTS', options].filter(
      Boolean,
    ),
};

// Опции запроса карточки: страница судьи грузит карточки нескольких турниров через useQueries
export function tournamentCardQueryOptions(clubId: string, tournamentId: string) {
  return {
    queryKey: QUERY_KEYS.tournamentCard(clubId, tournamentId),
    queryFn: async () => {
      const result = (
        await GET('/api/v1/user/clubs/{clubId}/tournaments/{tournamentId}', {
          params: {
            path: {
              clubId,
              tournamentId,
            },
          },
        })
      ).data!;

      // Команды сортируем по ключу
      const teams = [...result.teams];
      teams.sort((a, b) => {
        if (a.numberInTournament === null || b.numberInTournament === null) return 0;
        return a.numberInTournament - b.numberInTournament;
      });
      result.teams = teams;

      return result;
    },
  };
}

export function useTournamentCardQuery(
  clubId: string,
  tournamentId: string,
  { enabled = true, staleTime }: { enabled?: boolean; staleTime?: number } = {},
) {
  return useQuery({ enabled, staleTime, ...tournamentCardQueryOptions(clubId, tournamentId) });
}

export function updateTournamentCardQuery(
  ql: QueryClient,
  clubId: string,
  tournamentId: string,
  data: components['schemas']['TournamentDto'],
) {
  const key = QUERY_KEYS.tournamentCard(clubId, tournamentId);
  const prev = ql.getQueryData(key);
  ql.setQueryData(key, prev ? { ...prev, ...data } : data);
}

// Опции запроса активных участников: страница судьи грузит участников нескольких турниров
export function activeTournamentParticipantsQueryOptions(
  clubId: string,
  tournamentId: string,
  order: 'NumberAsc' | 'AlphabetAsc' | 'RatingDesc',
  isManager?: boolean,
) {
  const url = isManager
    ? '/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/participants'
    : '/api/v1/user/clubs/{clubId}/tournaments/{tournamentId}/participants';

  const status: components['schemas']['ParticipantStatus'][] = [
    'Participating',
    'Banned',
    'Removed',
  ];

  return {
    queryKey: QUERY_KEYS.tournamentParticipants(clubId, tournamentId, {
      status,
      order,
    }),
    queryFn: async () =>
      (
        await GET(url, {
          params: {
            path: { clubId, tournamentId },
            query: { status, order },
          },
        })
      ).data?.result as ParticipantDto[],
  };
}

export function useActiveTournamentParticipantsQuery(
  clubId: string,
  tournamentId: string,
  order: 'NumberAsc' | 'AlphabetAsc' | 'RatingDesc',
  isManager?: boolean,
  { refetchOnWindowFocus = true, enabled = true } = {},
) {
  return useQuery({
    enabled,
    refetchOnWindowFocus,
    ...activeTournamentParticipantsQueryOptions(clubId, tournamentId, order, isManager),
  });
}

export function useTournamentParticipantsQuery(
  clubId: string,
  tournamentId: string,
  status: components['schemas']['ParticipantStatus'],
  order?: 'NumberAsc' | 'AlphabetAsc' | 'RatingDesc',
) {
  return useQuery({
    structuralSharing: false,
    queryKey: QUERY_KEYS.tournamentParticipants(clubId, tournamentId, {
      status: [status],
    }),
    queryFn: async () =>
      (
        await GET('/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/participants', {
          params: {
            path: {
              clubId,
              tournamentId,
            },
            query: {
              status: [status],
              order,
              limit: 1000,
            },
          },
        })
      ).data?.result,
  });
}

export function useClubMembersForTournamentModalQuery(
  clubId: string,
  tournamentId: string,
  options: {
    order: 'AlphabetAsc' | 'RatingDesc';
    status?: 'JoinRequest' | 'Active' | 'Deleted' | 'Invited';
    query?: string;
    disabled?: boolean;
  },
) {
  const { order, status, query, disabled } = options;

  const result = useInfiniteQuery({
    enabled: !disabled,
    queryKey: [
      '/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/participants/members',
      clubId,
      tournamentId,
      order,
      status,
      query,
    ],
    getNextPageParam: (lastPage: { nextCursor?: number | null }) => lastPage.nextCursor,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      (
        await GET(
          '/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/participants/members',
          {
            params: {
              path: {
                clubId,
                tournamentId,
              },
              query: {
                query,
                order,
                status,
                limit: 30,
                cursor: pageParam ? Number(pageParam) : 0,
              },
            },
          },
        )
      ).data!,
  });

  return {
    ...result,
    data: result?.data?.pages?.reduce(
      (
        res: (components['schemas']['MemberTournamentParticipantDto'] & {
          participationStatus: ParticipantStatus;
        })[],
        p,
      ) =>
        res.concat(
          p.result as (components['schemas']['MemberTournamentParticipantDto'] & {
            participationStatus: ParticipantStatus;
          })[],
        ),
      [],
    ),
  };
}

export function useTournamentResultsQuery(clubId: string, tournamentId: string, enabled?: boolean) {
  return useQuery({
    enabled: !!enabled,
    queryKey: QUERY_KEYS.tournamentResultsAll(clubId, tournamentId),
    queryFn: async () =>
      (
        await GET('/api/v1/user/clubs/{clubId}/tournaments/{tournamentId}/results', {
          params: {
            path: {
              clubId,
              tournamentId,
            },
          },
        })
      ).data!,
  });
}

export function useRoundRobnResultsQuery(clubId: string, tournamentId: string, enabled?: boolean) {
  return useQuery({
    enabled: !!enabled,
    queryKey: QUERY_KEYS.tournamentResultsRoundRobin(clubId, tournamentId),
    queryFn: async () =>
      (
        await GET('/api/v1/user/clubs/{clubId}/tournaments/{tournamentId}/results/round-robin', {
          params: {
            path: {
              clubId,
              tournamentId,
            },
          },
        })
      ).data!,
  });
}

export function useSwissResultsQuery(clubId: string, tournamentId: string, enabled?: boolean) {
  return useQuery({
    enabled: !!enabled,
    queryKey: QUERY_KEYS.tournamentResultsSwiss(clubId, tournamentId),
    queryFn: async () =>
      (
        await GET('/api/v1/user/clubs/{clubId}/tournaments/{tournamentId}/results/swiss', {
          params: {
            path: {
              clubId,
              tournamentId,
            },
          },
        })
      ).data!,
  });
}

export function useSwissPrivateTeamResultsForTeamsQuery(
  clubId: string,
  tournamentId: string,
  enabled?: boolean,
) {
  return useQuery({
    enabled: !!enabled,
    queryKey: QUERY_KEYS.tournamentResultsSwissTeam(clubId, tournamentId),
    queryFn: async () =>
      (
        await GET(
          '/api/v1/user/clubs/{clubId}/tournaments/{tournamentId}/results/swiss/private-team/team',
          {
            params: {
              path: {
                clubId,
                tournamentId,
              },
            },
          },
        )
      ).data!,
  });
}

export function useTournamentRoundMovementPrivateQuery(
  clubId: string,
  tournamentId: string,
  roundNumber: number,
  enabled: boolean = true,
) {
  return useQuery({
    enabled,
    queryKey: ['tournamentResultsGames', clubId, tournamentId, roundNumber],
    queryFn: async () =>
      (
        await GET(
          '/api/v1/user/clubs/{clubId}/tournaments/{tournamentId}/results/rounds/{roundNumber}/private',
          {
            params: {
              path: {
                clubId,
                tournamentId,
                roundNumber: roundNumber as number,
              },
            },
          },
        )
      ).data!,
  });
}

export function useTournamentRoundMovementTeamQuery(
  clubId: string,
  tournamentId: string,
  roundNumber: number,
  enabled: boolean = true,
) {
  return useQuery({
    enabled,
    queryKey: ['tournamentResultsGames', clubId, tournamentId, roundNumber],
    queryFn: async () =>
      (
        await GET(
          '/api/v1/user/clubs/{clubId}/tournaments/{tournamentId}/results/rounds/{roundNumber}/team',
          {
            params: {
              path: {
                clubId,
                tournamentId,
                roundNumber: roundNumber as number,
              },
            },
          },
        )
      ).data!,
  });
}

export function useStartListPrivateQuery(
  clubId: string,
  tournamentId: string,
  enabled: boolean = true,
) {
  return useQuery({
    enabled,
    queryKey: [
      '/api/v1/user/clubs/{clubId}/tournaments/{tournamentId}/results/seeds/private',
      clubId,
      tournamentId,
    ],
    queryFn: async () =>
      (
        await GET('/api/v1/user/clubs/{clubId}/tournaments/{tournamentId}/results/seeds/private', {
          params: {
            path: { clubId, tournamentId },
          },
        })
      ).data,
  });
}

export function useStartListTeamQuery(
  clubId: string,
  tournamentId: string,
  enabled: boolean = true,
) {
  return useQuery({
    enabled,
    queryKey: [
      '/api/v1/user/clubs/{clubId}/tournaments/{tournamentId}/results/seeds/team',
      clubId,
      tournamentId,
    ],
    queryFn: async () =>
      (
        await GET('/api/v1/user/clubs/{clubId}/tournaments/{tournamentId}/results/seeds/team', {
          params: {
            path: { clubId, tournamentId },
          },
        })
      ).data,
  });
}

export function getRoundRobinXlsUrl(clubId: string, tournamentId: string) {
  const CLUB = '{clubId}';
  const TOURN = '{tournamentId}';
  const url: keyof paths = `/api/v1/user/clubs/${CLUB}/tournaments/${TOURN}/results/round-robin/excel`;

  return `${__RUNTIME.clubApi}${url.replace(CLUB, clubId).replace(TOURN, tournamentId)}`;
}

export function getSwissXlsUrl(clubId: string, tournamentId: string) {
  const CLUB = '{clubId}';
  const TOURN = '{tournamentId}';
  const url: keyof paths = `/api/v1/user/clubs/${CLUB}/tournaments/${TOURN}/results/swiss/excel`;

  return `${__RUNTIME.clubApi}${url.replace(CLUB, clubId).replace(TOURN, tournamentId)}`;
}

export function useTournamentRcfReportAvailabilityQuery(
  clubId: string,
  tournamentId: string,
  enabled?: boolean,
) {
  return useQuery({
    enabled,
    queryKey: [
      '/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/rcf-report/availability',
      clubId,
      tournamentId,
    ],
    queryFn: async () => {
      const result = await GET(
        `/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/rcf-report/availability`,
        {
          params: { path: { clubId, tournamentId } },
        },
      );
      return result.data;
    },
  });
}

export function useTournamentDownloadRcfReportMutation(clubId: string, tournamentId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationKey: [
      '/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/rcf-report',
      clubId,
      tournamentId,
    ],
    mutationFn: async () => {
      const result = await GET(
        `/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/rcf-report`,
        {
          params: { path: { clubId, tournamentId } },
          parseAs: 'blob',
        },
      );
      if (!result.response.ok) {
        throw result.error;
      }
      return result.data;
    },
    onError: report,
  });
}

export function useTournamentUploadRulesMutation(clubId: string, tournamentId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationKey: ['tournamentUploadRules', clubId, tournamentId],
    mutationFn: async (file: FormData) => {
      const result = await POST(`/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/rules`, {
        params: { path: { clubId, tournamentId } },
        // TODO fix openapi generation
        body: file as unknown as never,
      });
      if (!result.response.ok) {
        throw result.error;
      }
      return result;
    },
    onError: report,
  });
}

export function useTournamentUploadLogoMutation(clubId: string, tournamentId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationKey: ['tournamentUploadLogo', clubId, tournamentId],
    mutationFn: async (file: FormData) => {
      const result = await POST(`/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/logo`, {
        params: { path: { clubId, tournamentId } },
        // TODO fix openapi generation
        body: file as unknown as never,
      });
      if (!result.response.ok) {
        throw result.error;
      }
      return result;
    },
    onError: report,
  });
}

export function useTournamentUploadSponsorLogoMutation(clubId: string, tournamentId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationKey: ['tournamentUploadLogo', clubId, tournamentId],
    mutationFn: async ({
      sponsorNumber,
      formData,
    }: {
      sponsorNumber: number;
      formData: FormData;
    }) => {
      const result = await POST(
        `/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/sponsors/{sponsorNumber}/logo`,
        {
          params: { path: { clubId, tournamentId, sponsorNumber } },
          // TODO fix openapi generation
          body: formData as unknown as never,
        },
      );
      if (!result.response.ok) {
        throw result.error;
      }
      return result;
    },
    onError: report,
  });
}

export function useParticipantActionTournamentMutation(
  action: 'join' | 'leave',
  clubId: string,
  tournamentId: string,
) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async () => {
      return (
        await POST(
          `/api/v1/user/clubs/{clubId}/tournaments/{tournamentId}/participants/actions/${action}`,
          {
            params: { path: { clubId, tournamentId } },
          },
        )
      ).data!;
    },
    onError: report,
  });
}

export function useCreateTournamentMutation() {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({ clubId }: { clubId: string }) => {
      return (
        await POST('/api/v1/manager/clubs/{clubId}/tournaments', {
          params: { path: { clubId } },
        })
      ).data!;
    },
    onError: report,
  });
}

export function useCreateMatchMutation() {
  return useMutation({
    mutationFn: async ({ clubId }: { clubId: string }) => {
      return (
        await POST('/api/v1/manager/clubs/{clubId}/tournaments/one-on-one', {
          params: { path: { clubId } },
        })
      ).data!;
    },
  });
}

export function useUpdateTournamentMutation() {
  const ql = useQueryClient();
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({
      clubId,
      tournament,
    }: {
      clubId: string;
      tournament: UpdateTournamentRequest & { tournamentId: string };
    }) => {
      const data = (
        await POST('/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}', {
          params: {
            path: {
              clubId,
              tournamentId: tournament.tournamentId,
            },
          },
          body: tournament,
        })
      ).data!;

      updateTournamentCardQuery(ql, clubId, tournament.tournamentId, data);

      return data;
    },
    onError: report,
  });
}

export function useDeleteTournamentMutation() {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({ clubId, tournamentId }: { clubId: string; tournamentId: string }) => {
      await DELETE('/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}', {
        params: {
          path: {
            clubId,
            tournamentId,
          },
        },
      });
      return true;
    },
    onError: report,
  });
}

export function useAdminDeleteTournamentMutation() {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async (tournamentId: string) => {
      await DELETE('/api/v1/admin/state/tournaments/{tournamentId}/actions/delete-tournament', {
        params: {
          path: {
            tournamentId,
          },
        },
      });
      return true;
    },
    onError: report,
  });
}

export function usePublishTournamentMutation(clubId: string, tournamentId: string) {
  const { report } = useErrorNotification();
  const ql = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return (
        await POST('/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/actions/publish', {
          params: {
            path: {
              clubId,
              tournamentId,
            },
          },
        })
      ).data!;
    },
    onSuccess: () => {
      ql.invalidateQueries({
        queryKey: QUERY_KEYS.tournamentCard(clubId, tournamentId),
      });
    },
    onError: report,
  });
}

export function usePauseTournamentMutation(clubId: string, tournamentId: string) {
  const { report } = useErrorNotification();
  const ql = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return (
        await POST('/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/actions/pause', {
          params: {
            path: {
              clubId,
              tournamentId,
            },
          },
        })
      ).data!;
    },
    onSuccess: () => {
      ql.invalidateQueries({
        queryKey: QUERY_KEYS.tournamentCard(clubId, tournamentId),
      });
    },
    onError: report,
  });
}

export function useResumeTournamentMutation(clubId: string, tournamentId: string) {
  const { report } = useErrorNotification();
  const ql = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return (
        await POST('/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/actions/resume', {
          params: {
            path: {
              clubId,
              tournamentId,
            },
          },
        })
      ).data!;
    },
    onSuccess: () => {
      ql.invalidateQueries({
        queryKey: QUERY_KEYS.tournamentCard(clubId, tournamentId),
      });
    },
    onError: report,
  });
}

export function useCopyTournamentMutation(clubId: string, tournamentId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async () => {
      return (
        await POST('/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/copy', {
          params: {
            path: {
              clubId,
              tournamentId,
            },
          },
        })
      ).data;
    },
    onError: report,
  });
}

export function useCreateTournamentTeamMutation(clubId: string, tournamentId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      return (
        await POST('/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/teams', {
          params: {
            path: {
              clubId,
              tournamentId,
            },
          },
          body: {
            name,
          },
        })
      ).data!;
    },
    onError: report,
  });
}

export function useRenameTournamentTeamMutation(clubId: string, tournamentId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({ teamId, name }: { teamId: string; name: string }) => {
      return (
        await POST(
          '/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/teams/{teamId}/rename',
          {
            params: {
              path: {
                clubId,
                tournamentId,
                teamId,
              },
              query: { name },
            },
          },
        )
      ).data!;
    },
    onError: report,
  });
}

export function useBanTeamMutation(clubId: string, tournamentId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({ teamId }: { teamId: string }) => {
      return (
        await POST(
          '/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/teams/{teamId}/actions/ban',
          {
            params: {
              path: {
                clubId,
                tournamentId,
                teamId,
              },
            },
          },
        )
      ).data!;
    },
    onError: report,
  });
}

export function useDeleteTournamentTeamMutation(clubId: string, tournamentId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({ teamId }: { teamId: string }) => {
      return (
        await DELETE('/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/teams/{teamId}', {
          params: {
            path: {
              clubId,
              tournamentId,
              teamId,
            },
          },
        })
      ).data!;
    },
    onError: report,
  });
}

export function useRearrangeParticipantsMutation(clubId: string, tournamentId: string) {
  const ql = useQueryClient();
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({
      items,
    }: {
      items: components['schemas']['RearrangeParticipantsRequestItem'][];
    }) => {
      const data = (
        await POST(
          '/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/participants/rearrange',
          {
            params: { path: { clubId, tournamentId } },
            body: { items },
          },
        )
      ).data!;

      ql.setQueryData(
        QUERY_KEYS.tournamentParticipants(clubId, tournamentId, {
          status: ['Participating'],
        }),
        data,
      );

      return data;
    },
    onError: report,
  });
}

export function useRearrangeTeamsMutation(clubId: string, tournamentId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({ teamIds }: { teamIds: string[] }) => {
      const data = (
        await POST('/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/teams/rearrange', {
          params: { path: { clubId, tournamentId } },
          body: { teamIds },
        })
      ).data!;

      return data;
    },
    onError: report,
  });
}

export function useTournamentParticipantStatusMutation(clubId: string, tournamentId: string) {
  const { report } = useErrorNotification();

  return useMutation({
    mutationFn: async ({
      userId,
      action,
    }: {
      userId: string;
      action: 'add' | 'delete' | 'remove' | 'invite' | 'accept' | 'ban';
    }) => {
      return (
        await POST(
          `/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/participants/{userId}/actions/${action}`,
          {
            params: {
              path: {
                clubId,
                tournamentId,
                userId,
              },
            },
          },
        )
      ).data!;
    },
    onError: report,
  });
}

// Билет участника на свою видеокомнату: первый запрос закрепляет номер комнаты
export function useTournamentVideoTicketMutation(clubId: string, tournamentId: string) {
  const ql = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (
        await POST('/api/v1/user/clubs/{clubId}/tournaments/{tournamentId}/video-room-token', {
          params: { path: { clubId, tournamentId } },
        })
      ).data!.token,
    onSuccess: () => {
      ql.invalidateQueries({ queryKey: QUERY_KEYS.tournamentCard(clubId, tournamentId) });
    },
  });
}

// Билет судьи в указанную видеокомнату
// Билет судьи в видеокомнату: турнир в аргументах — мультикомната держит одну мутацию
// на все свои комнаты
export function useVideoRoomTicketMutation(clubId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({ tournamentId, roomNumber }: { tournamentId: string; roomNumber: number }) =>
      (
        await POST('/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/video-room-token', {
          params: { path: { clubId, tournamentId }, query: { roomNumber } },
        })
      ).data!.token,
    onError: report,
  });
}

// Билет судьи в приватную комнату игрока: турнир игрока в аргументах
export function useVideoPrivateTicketMutation(clubId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({ tournamentId, userId }: { tournamentId: string; userId: string }) =>
      (
        await POST(
          '/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/private-video-room-token',
          { params: { path: { clubId, tournamentId }, query: { userId } } },
        )
      ).data!.token,
    onError: report,
  });
}

// Служебные данные участника: флаг приватной комнаты `{ privateVideoRoom: true }`,
// null — вернуть в общую. Сам список участников обновится по событию менеджерам
export function useUpdateParticipantAuxMutation(clubId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({
      tournamentId,
      userId,
      aux,
    }: {
      tournamentId: string;
      userId: string;
      aux: ParticipantAux | null;
    }) => {
      await POST(
        '/api/v1/manager/clubs/{clubId}/tournaments/{tournamentId}/participants/{userId}/aux',
        {
          params: { path: { clubId, tournamentId, userId } },
          body: { aux },
        },
      );
    },
    onError: report,
  });
}

// Билет участника в свою приватную комнату. На ПК — сайтовым JWT (его подставит
// клиент), на телефоне без входа — групповым билетом как Bearer (схема `Video`)
export function useTournamentPrivateVideoTicketMutation(
  clubId: string,
  tournamentId: string,
  bearerTicket?: string,
) {
  return useMutation({
    mutationFn: async () =>
      (
        await POST(
          '/api/v1/user/clubs/{clubId}/tournaments/{tournamentId}/private-video-room-token',
          {
            params: { path: { clubId, tournamentId } },
            headers: bearerTicket ? { Authorization: `Bearer ${bearerTicket}` } : undefined,
          },
        )
      ).data!.token,
  });
}
